import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import React, { useEffect } from 'react'
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { SkeletonLoading } from '../../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { VerificationAgencyClientApplicationService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import AppSelectList, { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RelatedPartyValidationSchema } from './RelatedPartyValidationSchema';
import { AppQuill } from '../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { LoadingButton } from '@mui/lab';
import { RiskAnalysisTypeList } from '../../../../Component/Common/Constants/Lists/DropdownListOptions';
import { DataLookupService } from '../../../../Component/Api/DataLookup/DataLookupService';
import { RelatedPartyDto } from '../../../../Component/Model/Dto/VerificationAgency/Client/Application/RiskAssessment/RelatedParty';
import { toast } from 'react-toastify';
import { getIOptionsArray } from '../../../../Helpers/arrayHelper';
import AppCheckbox from '../../../../Component/Common/Components/FormValidationInputs/AppCheckbox';

interface IProps {
    relatedPartyId: number;
    riskAssessmentId: number;
    handleClose: () => void;
    showModal: boolean;
}

export const RelatedPartyModal = (props: IProps) => {

    const [isLoading, setIsLoading] = React.useState(false);
    const [userOptions, setUserOptions] = React.useState<IDropDownOptions[]>([]);
    const { control, handleSubmit, formState: { isSubmitting }, watch, reset, resetField } = useForm({
        mode: 'all',
        resolver: yupResolver(RelatedPartyValidationSchema),
    });
	const userInSystem = watch("userInSystem");

    const fetchRelatedParty = async () => {
        try {
            const id = props.relatedPartyId;
            if (id > 0) {
                const response = await VerificationAgencyClientApplicationService.GetRelatedPartyById(id);
                reset(response.data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchStaticData = async () => {
        try {
            const users = await DataLookupService.GetUsersInRegistrationList();
            const userOptions = getIOptionsArray(users.data, "Id", "Name");            
            setUserOptions(userOptions);
        } catch {
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        const relatedPartyPromise = fetchRelatedParty();
        const staticDataPromise = fetchStaticData();
        await Promise.all([relatedPartyPromise, staticDataPromise]);
        setIsLoading(false);
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const relatedPartyId = props.relatedPartyId;
            const formData = { ...data } as RelatedPartyDto;

            if(!userInSystem){
                formData.relatedPartyTo = null;
            }
            formData.id = relatedPartyId;
            formData.clientApplicationRiskAssessmentId = props.riskAssessmentId;

            if (relatedPartyId > 0) {
                await VerificationAgencyClientApplicationService.UpdateRelatedParty(formData);
                toast.success('Related Party saved'); 
            } else {
                await VerificationAgencyClientApplicationService.CreateRelatedParty(formData);
                toast.success('Related Party added'); 
            }
            props.handleClose();
        } catch { }
    };

    const onRelatedPartyCheck = () =>{
        if(userInSystem){
            resetField("relatedPartyTo", {defaultValue: null});
        }
    }
    useEffect(() => {
        fetchData();
    }, [props.relatedPartyId]);

    return (
        <Dialog disableEnforceFocus open={props.showModal} onClose={() => { props.handleClose() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="individual-dialog-title" onClose={() => props.handleClose()}>
                {props.relatedPartyId > 0 ? 'Edit' : 'Add'} Related Party
            </CustomDialogTitle>
            <DialogContent>
                {isLoading ?
                    <SkeletonLoading formLoading FieldAmount={6} />
                    :
                    <Grid container component="form" id="RelatedPartyForm" width='700px' spacing={2} paddingTop={1}
                        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                            handleSubmit(handleDataSubmit)(event);
                            if (event) {
                                if (typeof event.preventDefault === "function") {
                                    event.preventDefault();
                                }
                                if (typeof event.stopPropagation === "function") {
                                    event.stopPropagation();
                                }
                            }
                        }}
                    >
                        <Grid item xs={12} style={{paddingTop: "10px"}}>
                            <AppCheckbox control={control} label='The related party has an account in the system' name='userInSystem' onChange={onRelatedPartyCheck}/>
                        </Grid>
                        {userInSystem && 
                            <Grid item xs={12} style={{paddingTop: "10px"}}>
                                <AppSelectList control={control} name="relatedPartyTo" label='Related Party To' items={userOptions} />
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <MDTypography variant='span'>
                                Relationship
                            </MDTypography>
                            <AppQuill control={control} name="relationship" placeholder="Add text..." />
                        </Grid>
                        <Grid item xs={12}>
                            <AppSelectList control={control} name="riskId" label='Risk' items={RiskAnalysisTypeList} />
                        </Grid>
                        <Grid item xs={12}>
                            <MDTypography variant='span'>
                                How is the Risk Mitigated?
                            </MDTypography>
                            <AppQuill control={control} name="riskMitigated" placeholder="Add text..." />
                        </Grid>
                    </Grid>
                }
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={isSubmitting} variant='contained' form='RelatedPartyForm' type='submit' color='primary'>
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
